import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
      setMessage('Erreur lors du chargement des utilisateurs');
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };

  const loadUserSubscriptions = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users/${userId}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Erreur chargement abonnements:', error);
    }
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    await loadUserSubscriptions(user.id);
  };

  const handleEditClick = (user) => {
    setEditFormData({
      id: user.id,
      email: user.email,
      nom: user.nom,
      telephone: user.telephone,
      role: user.role
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/users/${editFormData.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('✅ Utilisateur modifié avec succès');
      setShowEditModal(false);
      loadUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ ' + (error.response?.data?.error || 'Erreur lors de la modification'));
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/admin/users/${selectedUser.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('✅ Utilisateur supprimé avec succès');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ ' + (error.response?.data?.error || 'Erreur lors de la suppression'));
    }
  };

  const generateM3U = async (userId, subId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/users/${userId}/subscriptions/${subId}/generate-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('✅ ' + response.data.message);
      loadUserSubscriptions(userId);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Erreur lors de la génération du lien');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('✅ Lien copié dans le presse-papier');
    setTimeout(() => setMessage(''), 2000);
  };

  const triggerWhatsAppReminder = (user) => {
    if (!user?.whatsapp) {
      setMessage('⚠️ Aucun numéro WhatsApp renseigné pour ce client');
      setTimeout(() => setMessage(''), 2500);
      return;
    }

    const phone = user.whatsapp.replace(/[^0-9]/g, '');
    const message = `Bonjour ${user.nom || 'client'},\n\nVotre abonnement Fluxivo expire bientôt.\n\n� Pour continuer à profiter de tous nos contenus, renouvelez votre accès dès maintenant.\n\n👉 Renouveler mon abonnement: http://51.254.135.114/plans\n\nMerci de votre confiance ! 🙏`;
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.telephone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-container">
          <div className="loading">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-container">
        <div className="page-header">
          <h1>Gestion des Utilisateurs</h1>
          {message && <div className="message">{message}</div>}
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <div className="stat-value">{stats.total_users}</div>
                <div className="stat-label">Utilisateurs</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📱</div>
              <div className="stat-info">
                <div className="stat-value">{stats.active_subscriptions}</div>
                <div className="stat-label">Abonnements actifs</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-value">{stats.total_revenue} FCFA</div>
                <div className="stat-label">Revenu total</div>
              </div>
            </div>
          </div>
        )}

        {/* Barre de recherche */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Rechercher un utilisateur (email, nom, téléphone)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Liste des utilisateurs */}
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Rôle</th>
                <th>Abonnements</th>
                <th>Date expiration</th>
                <th>Inscription</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.nom || 'N/A'}</td>
                  <td>{user.email}</td>
                  <td>{user.telephone || 'N/A'}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role === 'admin' ? '👑 Admin' : '👤 User'}
                    </span>
                  </td>
                  <td>
                    <span className="subscription-count">
                      {user.active_subscriptions}/{user.total_subscriptions}
                    </span>
                  </td>
                  <td>
                    {user.last_subscription_end ? (
                      <span className={isExpired(user.last_subscription_end) ? 'expired' : 'active'}>
                        {formatDate(user.last_subscription_end)}
                      </span>
                    ) : (
                      <span className="no-sub">Aucun</span>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => handleUserClick(user)}
                        title="Voir les détails"
                      >
                        👁️
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditClick(user)}
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteClick(user)}
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Détails de l'utilisateur sélectionné */}
        {selectedUser && !showEditModal && !showDeleteModal && (
          <div className="user-details-modal">
            <div className="modal-content large">
              <div className="modal-header">
                <h2>Détails de {selectedUser.nom || selectedUser.email}</h2>
                <button onClick={() => setSelectedUser(null)} className="close-btn">✕</button>
              </div>
              <div className="modal-body">
                <div className="user-info">
                  <p><strong>Email:</strong> {selectedUser.email}</p>
                  <p><strong>Téléphone:</strong> {selectedUser.telephone || 'N/A'}</p>
                  <p><strong>WhatsApp:</strong> {selectedUser.whatsapp || 'N/A'}</p>
                  <p><strong>Rôle:</strong> {selectedUser.role}</p>
                  <p><strong>Inscription:</strong> {formatDate(selectedUser.created_at)}</p>
                </div>

                {selectedUser.whatsapp && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      className="btn-generate"
                      onClick={() => triggerWhatsAppReminder(selectedUser)}
                      title="Envoyer un message de relance WhatsApp"
                    >
                      📱 Relance WhatsApp
                    </button>
                  </div>
                )}

                <h3>Abonnements ({subscriptions.length})</h3>
                {subscriptions.length === 0 ? (
                  <p className="no-data">Aucun abonnement</p>
                ) : (
                  <div className="subscriptions-list">
                    {subscriptions.map(sub => (
                      <div key={sub.id} className="subscription-card">
                        <div className="sub-header">
                          <span className={`status-badge ${sub.status}`}>
                            {sub.status === 'active' && !isExpired(sub.end_date) ? '✅ Actif' : '❌ Expiré'}
                          </span>
                          <span className="sub-plan">{sub.plan_type}</span>
                        </div>
                        <div className="sub-details">
                          <p><strong>Début:</strong> {formatDate(sub.start_date)}</p>
                          <p><strong>Fin:</strong> {formatDate(sub.end_date)}</p>
                          <p><strong>Montant:</strong> {sub.amount} FCFA</p>
                        </div>
                        {sub.m3u_url ? (
                          <div className="m3u-section">
                            <div className="m3u-url">
                              <input
                                type="text"
                                value={sub.m3u_url}
                                readOnly
                              />
                              <button
                                onClick={() => copyToClipboard(sub.m3u_url)}
                                className="btn-copy"
                              >
                                📋 Copier
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => generateM3U(selectedUser.id, sub.id)}
                            className="btn-generate"
                          >
                            🔗 Générer lien M3U
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal d'édition */}
        {showEditModal && (
          <div className="user-details-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Modifier l'utilisateur</h2>
                <button onClick={() => setShowEditModal(false)} className="close-btn">✕</button>
              </div>
              <form onSubmit={handleEditSubmit} className="modal-body">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={editFormData.nom}
                    onChange={(e) => setEditFormData({...editFormData, nom: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    value={editFormData.telephone}
                    onChange={(e) => setEditFormData({...editFormData, telephone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Rôle</label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                  >
                    <option value="user">Utilisateur</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowEditModal(false)} className="btn-cancel">
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">
                    💾 Enregistrer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression */}
        {showDeleteModal && selectedUser && (
          <div className="user-details-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Confirmer la suppression</h2>
                <button onClick={() => setShowDeleteModal(false)} className="close-btn">✕</button>
              </div>
              <div className="modal-body">
                <p className="warning-text">
                  ⚠️ Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser.email}</strong> ?
                </p>
                <p className="warning-text">
                  Cette action est irréversible et supprimera tous les abonnements et données associés.
                </p>
                <div className="modal-actions">
                  <button onClick={() => setShowDeleteModal(false)} className="btn-cancel">
                    Annuler
                  </button>
                  <button onClick={handleDelete} className="btn-delete-confirm">
                    🗑️ Supprimer définitivement
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
